import React, { useState } from "react";
import { variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import Modal from "./Modal";

const hasValue = (value) =>
  !(
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );

/**
 * Pushes chosen fields from the range onto every variant. It overwrites, so
 * it says so, and a field the range has not set is offered greyed out —
 * applying "nothing" would wipe what each variant already has.
 */
const ApplyToMembersDialog = ({ group, memberCount, onClose, onApplied }) => {
  const { t } = useI18n();
  const currency = useShopCurrency();
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // No cost_price at all means this member is not given buy prices
  const fields = [
    { key: "price", value: group.price, show: formatMoney(group.price, currency) },
    {
      key: "cost_price",
      value: group.cost_price,
      show: hasValue(group.cost_price) ? formatMoney(group.cost_price, currency) : "",
    },
    { key: "brand", value: group.brand, show: group.brand },
    { key: "category", value: group.category, show: group.category },
    { key: "subcategory", value: group.subcategory, show: group.subcategory },
    { key: "image", value: group.images, show: "" },
    {
      key: "details",
      value:
        group.juice_details || group.device_details || group.accessory_details,
      show: "",
    },
  ].filter((field) => field.key !== "cost_price" || field.value !== undefined);

  const toggle = (key) =>
    setChosen((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  const apply = async () => {
    setBusy(true);
    setError("");

    try {
      const { data } = await variantGroupsAPI.apply(group.id, chosen);
      onApplied(data.updated);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("ranges.apply.failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("ranges.apply.title")}
      onClose={onClose}
      maxWidthClass="max-w-lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          {t("ranges.apply.intro", { count: memberCount })}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {fields.map(({ key, value, show }) => {
            const available = hasValue(value);

            return (
              <li key={key}>
                <label
                  className={`flex items-center gap-3 px-4 py-3 ${
                    available ? "cursor-pointer" : "opacity-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!available}
                    checked={chosen.includes(key)}
                    onChange={() => toggle(key)}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="flex-1 text-sm font-medium text-gray-800">
                    {t(`ranges.apply.fields.${key}`)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {available ? show : t("ranges.apply.notSet")}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={busy || chosen.length === 0}
            onClick={apply}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {t("ranges.apply.confirm", { count: memberCount })}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyToMembersDialog;
